/**
 * Data layer for `igaming-pricing-separate-fully-custom`. Thin typed fetch wrappers over /api/v1/igaming-pricing-separate-fully-custom.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingPricingSeparateFullyCustomRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingPricingSeparateFullyCustomResponse {
	count: number;
	items: IgamingPricingSeparateFullyCustomRow[];
}

export interface CreateIgamingPricingSeparateFullyCustomBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-pricing-separate-fully-custom";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingPricingSeparateFullyCustomResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingPricingSeparateFullyCustomResponse>(res);
}

export async function create(body: CreateIgamingPricingSeparateFullyCustomBody): Promise<IgamingPricingSeparateFullyCustomRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingPricingSeparateFullyCustomRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingPricingSeparateFullyCustomQueryKeys = {
	all: ["igaming-pricing-separate-fully-custom"] as const,
	list: () => [...igamingPricingSeparateFullyCustomQueryKeys.all] as const,
	detail: (id: string) => [...igamingPricingSeparateFullyCustomQueryKeys.all, id] as const,
};
